import { CAPTURE_CLOCK_WINDOW_MS, CAPTURE_GAP_MS, MS_PER_SECOND } from '../constants/coSellerMic';

interface OriginEstimate {
  /** The stream position (samples) the estimate was taken at. */
  atSample: number;
  /** Client UTC (ms) of sample 0 according to that block's arrival. */
  originUtcMs: number;
}

/**
 * Client UTC capture time of every sample of the co-seller mic stream, for
 * the proxy's `capture_stamp` (C2). The web counterpart of the desktop apps'
 * `CaptureClock`.
 *
 * Each block of samples reaches the main thread at `nowUtcMs`
 * (`performance.timeOrigin + performance.now()`), when its last sample was
 * just captured; so sample 0 was captured at now minus the samples buffered so
 * far. The main thread only ever receives a block LATE (never early), so the
 * clock keeps the EARLIEST such estimate over the last
 * CAPTURE_CLOCK_WINDOW_MS of audio: that filters the scheduling jitter, and
 * the window lets it follow a slow drift between the audio and wall clocks.
 * An estimate CAPTURE_GAP_MS later than the clock means samples never arrived
 * (the capture stalled): the clock re-anchors there and reports one
 * discontinuity, so the next frame carries a fresh stamp.
 */
export class CaptureClock {
  private estimates: OriginEstimate[] = [];
  private hasDiscontinuity = false;
  private readonly windowSamples: number;

  constructor(private readonly sampleRate: number) {
    if (!(sampleRate > 0)) throw new Error(`Capture clock sample rate must be positive, got ${sampleRate}`);
    this.windowSamples = (sampleRate * CAPTURE_CLOCK_WINDOW_MS) / MS_PER_SECOND;
  }

  /** A block ending at stream position `endSample` reached the main thread at `nowUtcMs`. */
  observe(endSample: number, nowUtcMs: number): void {
    const originUtcMs = nowUtcMs - this.samplesToMs(endSample);
    const current = this.originUtcMs();
    if (current !== null && originUtcMs - current > CAPTURE_GAP_MS) {
      this.estimates = [];
      this.hasDiscontinuity = true;
    }
    this.estimates.push({ atSample: endSample, originUtcMs });
    this.estimates = this.estimates.filter((estimate) => endSample - estimate.atSample <= this.windowSamples);
  }

  /** Client UTC (ms) of stream position `sample`; null before the first block. */
  utcAtSample(sample: number): number | null {
    const origin = this.originUtcMs();
    return origin === null ? null : origin + this.samplesToMs(sample);
  }

  /** True once after a capture gap: the caller stamps the next frame. */
  takeDiscontinuity(): boolean {
    const hadDiscontinuity = this.hasDiscontinuity;
    this.hasDiscontinuity = false;
    return hadDiscontinuity;
  }

  private originUtcMs(): number | null {
    if (this.estimates.length === 0) return null;
    return Math.min(...this.estimates.map((estimate) => estimate.originUtcMs));
  }

  private samplesToMs(samples: number): number {
    return (samples / this.sampleRate) * MS_PER_SECOND;
  }
}
